import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Box from 'apollo-react/components/Box';
import Typography from 'apollo-react/components/Typography';
import CircularProgress from 'apollo-react/components/CircularProgress';
import Button from 'apollo-react/components/Button';
import Paper from 'apollo-react/components/Paper';
import StatusCheck from 'apollo-react-icons/StatusCheck';
import StatusNegative from 'apollo-react-icons/StatusNegative';
import axios from 'axios';
import styles from './PaymentProcessing.module.css';

const POLLING_INTERVAL = 1500;
const MAX_POLLING_ATTEMPTS = 40;

// Use a local axios instance to avoid global 404 redirects
const paymentAxios = axios.create({
  // eslint-disable-next-line no-undef
  baseURL: process.env.REACT_APP_SERVER_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

const PaymentProcessing = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [status, setStatus] = useState('verifying');
  const [message, setMessage] = useState(t('payment.verifying'));
  const [countdown, setCountdown] = useState(3);
  const [error, setError] = useState(null);
  const [orderId, setOrderId] = useState(null);

  const pollingAttemptsRef = useRef(0);
  const pollingIntervalRef = useRef(null);
  const countdownIntervalRef = useRef(null);
  const hasProcessedRef = useRef(false);

  // Support both our transaction_id and Paymob's order_id
  const transactionId =
    searchParams.get('transaction_id') ||
    searchParams.get('transactionId') ||
    searchParams.get('order_id') ||
    searchParams.get('order') ||
    searchParams.get('id');
  const draftId = searchParams.get('draft_id');
  const workflowVersionId = searchParams.get('workflow_version_id');
  const requestName = searchParams.get('request_name');
  const successParam = searchParams.get('success');
  const requestIdParam =
    searchParams.get('request_id') ||
    sessionStorage.getItem('payment_request_id');

  // Get temp draft info from sessionStorage
  const getTempDraft = useCallback(() => {
    const tempDraftKey = sessionStorage.getItem('payment_temp_draft_key');
    if (tempDraftKey) {
      // TODO: Implement server-side draft retrieval
      return null;
    }
    return null;
  }, []);

  // Clear payment-related sessionStorage
  const clearPaymentStorage = useCallback(() => {
    sessionStorage.removeItem('payment_temp_draft_key');
    sessionStorage.removeItem('payment_transaction_id');
    sessionStorage.removeItem('payment_request_id');
  }, []);

  // Handle success redirect
  const handleSuccessRedirect = useCallback(() => {
    // Clear all intervals before redirect
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
    clearPaymentStorage();
    navigate('/customer/requests/my-requests?tab=0', { replace: true });
  }, [clearPaymentStorage, navigate]);

  // Handle failure redirect
  const handleFailureRedirect = useCallback(() => {
    // Clear all intervals before redirect
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }

    const tempDraft = getTempDraft();
    const tempDraftKey = sessionStorage.getItem('payment_temp_draft_key');

    if (requestIdParam) {
      const params = new URLSearchParams();
      if (draftId) params.set('draft_id', draftId);
      navigate(`/customer/progress/requests/${requestIdParam}?${params}`, {
        replace: true,
      });
      sessionStorage.removeItem('payment_transaction_id');
      sessionStorage.removeItem('payment_request_id');
    } else if (tempDraft && tempDraft.workflowVersionId) {
      const params = new URLSearchParams();
      if (tempDraftKey) params.set('draft_id', tempDraftKey);
      if (requestName) params.set('request_name', requestName);
      params.set('paymentFailed', 'true');
      navigate(
        `/customer/requests/new-requests/view/${tempDraft.workflowVersionId}?${params}`,
        {
          replace: true,
        }
      );

      sessionStorage.removeItem('payment_transaction_id');
      sessionStorage.removeItem('payment_request_id');
    } else if (workflowVersionId) {
      const params = new URLSearchParams();
      if (draftId) params.set('draft_id', draftId);
      if (requestName) params.set('request_name', requestName);
      params.set('paymentFailed', 'true');
      navigate(
        `/customer/requests/new-requests/view/${workflowVersionId}?${params}`,
        {
          replace: true,
        }
      );

      sessionStorage.removeItem('payment_transaction_id');
      sessionStorage.removeItem('payment_request_id');
    } else {
      clearPaymentStorage();
      navigate('/customer/requests/new-requests', { replace: true });
    }
  }, [
    getTempDraft,
    clearPaymentStorage,
    navigate,
    workflowVersionId,
    draftId,
    requestName,
    requestIdParam,
  ]);

  // Poll for payment status
  const pollPaymentStatus = useCallback(async () => {
    if (!transactionId || hasProcessedRef.current) {
      return;
    }

    try {
      pollingAttemptsRef.current += 1;

      const response = await paymentAxios.get(
        `/api/payments/status/${transactionId}`
      );

      const { status: paymentStatus, order_id: responseOrderId } =
        response.data?.data || {};

      if (responseOrderId && !orderId) {
        setOrderId(responseOrderId);
      }

      if (paymentStatus === 'SUCCEEDED') {
        hasProcessedRef.current = true;
        clearInterval(pollingIntervalRef.current);
        setStatus('succeeded');
        setMessage(t('payment.successMessage'));

        countdownIntervalRef.current = setInterval(() => {
          setCountdown((prev) => {
            if (prev <= 1) {
              clearInterval(countdownIntervalRef.current);
              handleSuccessRedirect();
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      } else if (paymentStatus === 'FAILED') {
        hasProcessedRef.current = true;
        clearInterval(pollingIntervalRef.current);
        setStatus('failed');
        setMessage(t('payment.declinedMessage'));

        countdownIntervalRef.current = setInterval(() => {
          setCountdown((prev) => {
            if (prev <= 1) {
              clearInterval(countdownIntervalRef.current);
              handleFailureRedirect();
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      } else if (pollingAttemptsRef.current >= MAX_POLLING_ATTEMPTS) {
        hasProcessedRef.current = true;
        clearInterval(pollingIntervalRef.current);
        setStatus('failed');
        setMessage(t('payment.timeoutMessage'));
        setError('timeout');
      }
    } catch (err) {
      console.error('Error polling payment status:', err);
      if (pollingAttemptsRef.current >= MAX_POLLING_ATTEMPTS) {
        hasProcessedRef.current = true;
        clearInterval(pollingIntervalRef.current);
        setStatus('failed');
        setMessage(t('payment.networkErrorMessage'));
        setError('network');
      }
    }
  }, [transactionId, handleSuccessRedirect, handleFailureRedirect, t, orderId]);

  // Check direct success param from gateway redirect
  useEffect(() => {
    if (successParam === 'true' && !hasProcessedRef.current) {
      setMessage(t('payment.completedVerifyingMessage'));
    } else if (successParam === 'false' && !hasProcessedRef.current) {
      hasProcessedRef.current = true;
      setStatus('failed');
      setMessage(t('payment.failedTitle', 'Payment Failed'));

      setTimeout(() => {
        handleFailureRedirect();
      }, 2000);
    }
  }, [successParam, handleFailureRedirect, t]);

  // Start polling on mount
  useEffect(() => {
    if (!transactionId) {
      setStatus('failed');
      setMessage(t('payment.invalidSessionMessage'));
      setError('no_transaction');
      return;
    }

    pollingIntervalRef.current = setInterval(
      pollPaymentStatus,
      POLLING_INTERVAL
    );

    pollPaymentStatus();

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
      }
    };
  }, [transactionId, pollPaymentStatus, t]);

  const renderIcon = () => {
    switch (status) {
      case 'succeeded':
        return (
          <StatusCheck className={`${styles.statusIcon} ${styles.success}`} />
        );
      case 'failed':
        return (
          <StatusNegative className={`${styles.statusIcon} ${styles.error}`} />
        );
      default:
        return <CircularProgress variant="indeterminate" size={80} />;
    }
  };

  const getStatusColor = () => {
    switch (status) {
      case 'succeeded':
        return '#4caf50';
      case 'failed':
        return '#f44336';
      default:
        return '#0768fd';
    }
  };

  return (
    <Box className={styles.container}>
      {/* Logo Container - Above Card */}
      <Box className={styles.logoContainer}>
        <Typography variant="h4" className={styles.logoTitle}>
          Workflow Engine
        </Typography>
        <Typography variant="caption" className={styles.logoSubtitle}>
          Payment Processing
        </Typography>
      </Box>

      <Paper elevation={3} className={styles.card}>
        <Box className={styles.content}>
          <Box
            className={styles.iconContainer}
            style={{ color: getStatusColor() }}
          >
            {renderIcon()}
          </Box>

          <Typography variant="h4" className={styles.statusTitle}>
            {status === 'verifying' && t('payment.processingPaymentTitle')}
            {status === 'succeeded' && t('payment.successTitle')}
            {status === 'failed' && t('payment.failedTitle', 'Payment Failed')}
          </Typography>

          <Typography variant="body1" className={styles.statusMessage}>
            {message}
          </Typography>

          {status !== 'verifying' && countdown > 0 && (
            <Typography variant="body2" className={styles.redirectNotice}>
              {t('payment.redirectingIn', { seconds: countdown })}
            </Typography>
          )}

          {status === 'verifying' && (
            <Typography variant="body2" className={styles.waitNotice}>
              {t('payment.waitNotice')}
            </Typography>
          )}

          {error === 'timeout' && (
            <Box className={styles.actionButtons}>
              <Button
                variant="outlined"
                onClick={() =>
                  navigate('/customer/requests/my-requests', { replace: true })
                }
              >
                {t('payment.goToMyRequests')}
              </Button>
            </Box>
          )}

          {status === 'failed' && error !== 'timeout' && (
            <Box className={styles.actionButtons}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleFailureRedirect}
              >
                {t('payment.tryAgain')}
              </Button>
              <Button
                variant="outlined"
                onClick={() =>
                  navigate('/customer/requests/my-requests', { replace: true })
                }
              >
                {t('Cancel')}
              </Button>
            </Box>
          )}

          {status === 'succeeded' && (
            <Button
              variant="text"
              onClick={handleSuccessRedirect}
              className={styles.manualRedirect}
            >
              {t('payment.clickToRedirect')}
            </Button>
          )}
        </Box>

        {/* Footer */}
        <Box className={styles.footer}>
          {orderId && (
            <Typography variant="caption" className={styles.orderId}>
              {t('payment.reference', { orderId })}
            </Typography>
          )}
          <Typography variant="caption" className={styles.footerText}>
            {t('payment.copyright', { year: new Date().getFullYear() })}
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
};

export default PaymentProcessing;
